/**
 * Storage das chaves de sessao — web e nativo.
 *
 * POR QUE EXISTE
 * No iOS o WKWebView faz eviction do localStorage: a usuaria e deslogada
 * sozinha depois de algumas horas sem abrir o app. E a causa raiz que o
 * pwa/authKeeper.js contorna hoje tratando o sintoma. No app nativo o token
 * passa a viver no storage do sistema (Capacitor Preferences), que nao e
 * despejado.
 *
 * POR QUE A API E SINCRONA
 * Preferences e assincrono, mas o interceptor do api.js le o token dentro de
 * uma funcao sincrona, e tornar isso async espalharia await por todo o caminho
 * critico do site em producao. Em vez disso mantemos um cache em memoria:
 *
 *   - boot nativo  -> hydrate() carrega tudo do Preferences UMA vez (await)
 *   - leitura      -> sincrona, do cache
 *   - escrita      -> cache na hora + persistencia em background
 *
 * Na web nao ha cache nenhum: le e escreve direto no localStorage, exatamente
 * como antes. O comportamento do site fica inalterado por construcao.
 */

import { isNative, getPlugin } from './native';

// Somente chaves de sessao. Preferencias de UI (onboarding_completo,
// help_fab_disabled) seguem no localStorage: perde-las e inofensivo.
export const CHAVES_SESSAO = [
  'token',
  'refresh_token',
  'is_admin',
  'is_superadmin',
  'tenant',
  'user_email',
];

const cache = new Map();
let hidratado = false;

function prefs() {
  return getPlugin('Preferences');
}

/** Persiste em background. Falha aqui nunca deve quebrar o fluxo de auth. */
function persistir(chave, valor) {
  const p = prefs();
  if (!p) return;
  const op = valor === null
    ? p.remove({ key: chave })
    : p.set({ key: chave, value: valor });
  Promise.resolve(op).catch(() => {});
}

/**
 * Carrega as chaves de sessao do storage nativo para o cache.
 * Chamar UMA vez no boot, antes de decidir se o usuario esta autenticado.
 * Na web e no-op — resolve imediatamente.
 */
export async function hydrate() {
  if (!isNative() || hidratado) return;
  const p = prefs();
  if (!p) {
    // Plugin ausente (ainda nao instalado no projeto nativo): cai no
    // localStorage do webview. Funciona, so nao sobrevive ao eviction do iOS.
    console.log('[PWA] storage-fallback-localstorage');
    hidratado = true;
    return;
  }
  await Promise.all(CHAVES_SESSAO.map(async (chave) => {
    try {
      const { value } = await p.get({ key: chave });
      if (value !== null && value !== undefined) cache.set(chave, value);
    } catch { /* chave ausente ou storage indisponivel: segue sem */ }
  }));
  hidratado = true;
  console.log('[PWA] storage-hydrated', { chaves: cache.size });
}

export function getItem(chave) {
  if (!isNative() || !prefs()) return localStorage.getItem(chave);
  return cache.has(chave) ? cache.get(chave) : null;
}

export function setItem(chave, valor) {
  if (!isNative() || !prefs()) {
    localStorage.setItem(chave, valor);
    return;
  }
  cache.set(chave, valor);
  persistir(chave, valor);
}

export function removeItem(chave) {
  if (!isNative() || !prefs()) {
    localStorage.removeItem(chave);
    return;
  }
  cache.delete(chave);
  persistir(chave, null);
}

/** Limpa toda a sessao (logout). */
export function clearSession() {
  CHAVES_SESSAO.forEach(removeItem);
}
